/* eslint-disable no-nested-ternary */
import { useMemo, useState, useCallback } from 'react';
import { capitalCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetTopic3ContentList,
  selectTopic3ContentPageNumber,
  selectGetTopic3ContentState,
  selectTopic3HasMoreContent,
  selectTopic3ContentList,
  UpdateTopic3HasMoreContent,
  UpdateTopic3PageNumber,
} from '../../../store/slices/contentSlice';
import { getTopic3Content } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface Topic3ContentCategoryProps {
  categoryId: string;
  topicName: string;
  setSomeContentLoadedInitially: (value: boolean) => void;
  contentFeedSection: ContentFeedSection
  viewMorePage?: boolean
}

export function Topic3ContentCategory({
  categoryId,
  topicName,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:Topic3ContentCategoryProps) {
  const storeTopic3ContentList = useAppSelector(selectTopic3ContentList);
  const topic3ContentList = useMemo(() => storeTopic3ContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeTopic3ContentList, contentFeedSection]);

  const getTopic3ContentState = useAppSelector(selectGetTopic3ContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const topic3Page = useAppSelector(selectTopic3ContentPageNumber);
  const topic3HasMore = useAppSelector(selectTopic3HasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: topic3ContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { contentTopics: topicName },
    getCategoryContent: getTopic3Content,
    resetCategoryContentList: ResetTopic3ContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateTopic3PageNumber,
    updateHasMoreCategoryContent: UpdateTopic3HasMoreContent,
  });

  const isLoading = useMemo(
    () => (getTopic3ContentState === 'pending' && !initialFetchDone),
    [getTopic3ContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      topic3Page,
      topic3HasMore,
    );
  }, [topic3Page, topic3HasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${topicName}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${categoryId}-content`}
          categoryId={categoryId}
          storeContentList={topic3ContentList}
          onNextPage={onNextPage}
          hasMoreContent={topic3HasMore}
          showSearchBar={true}
        />
      ) : topic3ContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${topicName}-content`}
          categoryTitle={capitalCase(topicName)}
          storeContentList={topic3ContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
